<template>
  <div class="composite-action">
    <div class="composite-action__row">
      <div
        class="composite-action__field composite-action__field--grow multiselect-wrap--small"
        :class="{ 'has-error': hasError('PIPELINE_REQUIRED') }"
      >
        <label class="composite-action__label">
          {{ $t('AUTOMATION.ACTION.CREATE_OPPORTUNITY.PIPELINE') }}
        </label>
        <multiselect
          :value="selectedPipeline"
          track-by="id"
          label="name"
          :placeholder="$t('FORMS.MULTISELECT.SELECT')"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          :max-height="160"
          :options="pipelineOptions"
          :allow-empty="false"
          @input="onPipelineSelect"
        />
      </div>
      <div
        class="composite-action__field composite-action__field--grow multiselect-wrap--small"
        :class="{ 'has-error': hasError('STAGE_REQUIRED') }"
      >
        <label class="composite-action__label">
          {{ $t('AUTOMATION.ACTION.CREATE_OPPORTUNITY.STAGE') }}
        </label>
        <multiselect
          :value="selectedStage"
          track-by="id"
          label="name"
          :placeholder="
            selectedPipeline
              ? $t('FORMS.MULTISELECT.SELECT')
              : $t('AUTOMATION.ACTION.CREATE_OPPORTUNITY.SELECT_PIPELINE_FIRST')
          "
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          :max-height="160"
          :options="stageOptions"
          :disabled="!selectedPipeline"
          :allow-empty="false"
          @input="update({ pipeline_stage_id: $event.id })"
        />
      </div>
      <div
        class="composite-action__field composite-action__field--value"
        :class="{ 'has-error': hasError('VALUE_INVALID') }"
      >
        <label class="composite-action__label">
          {{ $t('AUTOMATION.ACTION.CREATE_OPPORTUNITY.VALUE') }}
        </label>
        <decimal-input
          :value="params.value"
          :placeholder="
            $t('AUTOMATION.ACTION.CREATE_OPPORTUNITY.VALUE_PLACEHOLDER')
          "
          @input="update({ value: $event })"
        />
      </div>
    </div>
    <div class="composite-action__row">
      <div
        class="composite-action__field composite-action__field--grow multiselect-wrap--small"
        :class="{ 'has-error': hasError('OWNER_STRATEGY_REQUIRED') }"
      >
        <label class="composite-action__label">
          {{ $t('AUTOMATION.ACTION.CREATE_OPPORTUNITY.OWNER_STRATEGY') }}
        </label>
        <multiselect
          :value="selectedOwnerStrategy"
          track-by="id"
          label="name"
          :placeholder="$t('FORMS.MULTISELECT.SELECT')"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          :max-height="200"
          :options="ownerStrategyOptions"
          :allow-empty="false"
          @input="onOwnerStrategySelect"
        />
      </div>
      <div
        v-if="params.owner_strategy === 'specific_user'"
        class="composite-action__field composite-action__field--grow multiselect-wrap--small"
        :class="{
          'has-error':
            hasError('OWNER_REQUIRED') || hasError('OWNER_NOT_IN_PIPELINE'),
        }"
      >
        <label class="composite-action__label">
          {{ $t('AUTOMATION.ACTION.CREATE_OPPORTUNITY.OWNER') }}
        </label>
        <multiselect
          :value="selectedOwner"
          track-by="id"
          label="name"
          :placeholder="
            selectedPipeline
              ? $t('FORMS.MULTISELECT.SELECT')
              : $t('AUTOMATION.ACTION.CREATE_OPPORTUNITY.SELECT_PIPELINE_FIRST')
          "
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          :max-height="160"
          :options="ownerOptions"
          :disabled="!selectedPipeline"
          :allow-empty="false"
          @input="update({ owner_id: $event.id })"
        />
      </div>
    </div>
    <p v-if="ownerStrategyHint" class="composite-action__hint">
      {{ ownerStrategyHint }}
    </p>
    <p class="composite-action__hint">
      {{ $t('AUTOMATION.ACTION.CREATE_OPPORTUNITY.HINT') }}
    </p>
    <ul v-if="visibleErrors.length" class="composite-action__errors">
      <li v-for="error in visibleErrors" :key="error">
        {{ errorMessage(error) }}
      </li>
    </ul>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import DecimalInput from 'dashboard/components/widgets/forms/DecimalInput.vue';
import {
  OPPORTUNITY_OWNER_STRATEGIES,
  compositeActionParams,
  createOpportunityActionErrors,
  findPipeline,
  isPipelineMember,
} from 'dashboard/helper/automationOpportunityActionHelper';

const ALWAYS_VISIBLE_ERRORS = [
  'OWNER_NOT_IN_PIPELINE',
  'MANAGER_NOT_IN_PIPELINE',
];

const sortByPosition = stages =>
  [...stages].sort((a, b) => (a.position || 0) - (b.position || 0));

export default {
  name: 'AutomationActionCreateOpportunityInput',
  components: { DecimalInput },
  props: {
    value: {
      type: [Object, Array],
      default: () => ({}),
    },
    pipelines: {
      type: Array,
      default: () => [],
    },
    showErrors: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({ agents: 'agents/getAgents' }),
    params() {
      return compositeActionParams(this.value);
    },
    selectedPipeline() {
      return findPipeline(this.pipelines, this.params.pipeline_id);
    },
    pipelineOptions() {
      return this.pipelines
        .filter(
          pipeline =>
            pipeline.status === 'active' ||
            pipeline.id === this.params.pipeline_id
        )
        .map(pipeline => ({
          id: pipeline.id,
          name:
            pipeline.status === 'active'
              ? pipeline.name
              : this.$t('AUTOMATION.ACTION.CREATE_OPPORTUNITY.INACTIVE', {
                  name: pipeline.name,
                }),
        }));
    },
    stageOptions() {
      const stages = sortByPosition(this.selectedPipeline?.stages || []);
      return stages
        .filter(
          stage =>
            stage.status !== 'inactive' ||
            stage.id === this.params.pipeline_stage_id
        )
        .map(stage => ({ id: stage.id, name: stage.name }));
    },
    selectedStage() {
      return (
        this.stageOptions.find(
          stage => stage.id === this.params.pipeline_stage_id
        ) || null
      );
    },
    ownerStrategyOptions() {
      return OPPORTUNITY_OWNER_STRATEGIES.map(strategy => ({
        id: strategy,
        name: this.$t(
          `AUTOMATION.ACTION.CREATE_OPPORTUNITY.OWNER_STRATEGIES.${strategy.toUpperCase()}`
        ),
      }));
    },
    selectedOwnerStrategy() {
      return (
        this.ownerStrategyOptions.find(
          strategy => strategy.id === this.params.owner_strategy
        ) || null
      );
    },
    ownerOptions() {
      if (!this.selectedPipeline) return [];
      return (this.agents || [])
        .filter(agent => isPipelineMember(this.selectedPipeline, agent.id))
        .map(agent => ({ id: agent.id, name: agent.name }));
    },
    selectedOwner() {
      const owner = (this.agents || []).find(
        agent => agent.id === this.params.owner_id
      );
      return owner ? { id: owner.id, name: owner.name } : null;
    },
    ownerStrategyHint() {
      if (!this.params.owner_strategy) return '';
      return this.$t(
        `AUTOMATION.ACTION.CREATE_OPPORTUNITY.OWNER_STRATEGY_HINTS.${this.params.owner_strategy.toUpperCase()}`
      );
    },
    errors() {
      return createOpportunityActionErrors(this.params, this.pipelines);
    },
    visibleErrors() {
      return this.errors.filter(
        error => this.showErrors || ALWAYS_VISIBLE_ERRORS.includes(error)
      );
    },
  },
  methods: {
    update(changes) {
      this.$emit('input', { ...this.params, ...changes });
    },
    hasError(error) {
      return this.showErrors && this.errors.includes(error);
    },
    errorMessage(error) {
      return this.$t(`AUTOMATION.ACTION.CREATE_OPPORTUNITY.ERRORS.${error}`, {
        name: this.selectedPipeline?.manager?.name || '',
      });
    },
    onPipelineSelect(option) {
      const pipeline = findPipeline(this.pipelines, option.id);
      const firstStage = sortByPosition(pipeline?.stages || []).find(
        stage => stage.status !== 'inactive'
      );
      const keepsOwner = isPipelineMember(pipeline, this.params.owner_id);

      this.update({
        pipeline_id: option.id,
        pipeline_stage_id: firstStage ? firstStage.id : null,
        owner_id: keepsOwner ? this.params.owner_id : null,
      });
    },
    onOwnerStrategySelect(option) {
      this.update({
        owner_strategy: option.id,
        owner_id: option.id === 'specific_user' ? this.params.owner_id : null,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.composite-action {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  margin-top: var(--space-small);
}

.composite-action__row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-small);
}

.composite-action__field {
  display: flex;
  flex-direction: column;
  margin: 0;
  min-width: 0;

  &.has-error .composite-action__label {
    color: var(--r-500);
  }
}

.composite-action__field--grow {
  flex: 1 1 20rem;
}

.composite-action__field--value {
  flex: 0 0 16rem;
}

.composite-action__label {
  color: var(--s-700);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-micro);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.composite-action__hint {
  color: var(--s-600);
  font-size: var(--font-size-mini);
  margin: 0;
}

.composite-action__errors {
  color: var(--r-500);
  font-size: var(--font-size-small);
  list-style: none;
  margin: 0;
}
</style>
